import { ExclamationCircleIcon as ExclamationCircleIconOutline } from '@heroicons/react/24/outline';
import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import React, { PropsWithChildren } from 'react';

import { Tooltip } from '@/components/Tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/utils/classname';

export interface FormInfo {
  url?: string;
  text: string;
  content?: React.ReactNode;
}

interface FormGroupProps extends PropsWithChildren {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  label?: React.ReactElement<any> | string;
  error?: string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  name: string;
  info?: FormInfo;
  className?: string;
  formClassName?: string;
  isLoading?: boolean;
}

export const FormGroup = (props: FormGroupProps) => {
  const {
    error = '',
    label = '',
    children,
    endIcon,
    startIcon,
    name,
    isLoading,
    className,
    formClassName,
    info,
  } = props;
  const rightIcon = error || endIcon;
  const [showInfo, setShowInfo] = React.useState(false);

  return (
    <div className={formClassName}>
      {label && (
        <div className="mb-1 flex items-center gap-1">
          <label
            htmlFor={name}
            className="text-chariot-text-primary block text-sm leading-6 font-medium"
          >
            {label}
          </label>
          {info && (
            <Tooltip
              placement="right"
              title={info?.text}
              tooltipClassName="max-w-xs"
            >
              <ExclamationCircleIconOutline
                className="size-4"
                aria-hidden="true"
                onClick={() =>
                  info.url ? window.open(info.url) : setShowInfo(info => !info)
                }
              />
            </Tooltip>
          )}
        </div>
      )}
      <div
        className={cn(
          'text-default-light relative rounded-[2px] shadow-xs',
          className
        )}
      >
        {startIcon && (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            {startIcon}
          </div>
        )}
        {children}
        {rightIcon && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            {error && (
              <ExclamationCircleIcon
                className="size-5 text-red-500"
                aria-hidden="true"
              />
            )}
            {endIcon}
          </div>
        )}
        {isLoading && (
          <Skeleton shape="block" className="absolute top-0 left-0 size-full" />
        )}
      </div>
      {info && info.content && showInfo && (
        <div
          className={'bg-chariot-background-secondary mt-2 rounded p-2 text-sm'}
        >
          {info.content}
        </div>
      )}
      {error && (
        <p className="mt-2 text-sm text-red-600" id={`${name}-error`}>
          {error}
        </p>
      )}
    </div>
  );
};
